import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { LoginSuccessAnimation } from '@/components/animations/LoginSuccessAnimation';
import { login as loginService, validateMfa } from '@/services/auth';
import { getErrorMessage, getErrorDetails, isApiException } from '@/utils/errorHandler';

// ─── Helpers ───────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

function generateDeviceFingerprint(): string {
  const nav = window.navigator;
  const raw = [
    nav.userAgent,
    nav.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
  ].join('|');
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return `WEB-${Math.abs(hash).toString(16).toUpperCase()}`;
}

function formatDoc(value: string, tipo: 'cpf' | 'cnpj'): string {
  if (tipo === 'cpf') {
    const d = value.replace(/\D/g, '').slice(0, 11);
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  const d = value.replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

function validateCpfCnpj(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    if (/^(\d)\1{10}$/.test(digits)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
    let d = 11 - (sum % 11);
    if (d >= 10) d = 0;
    if (d !== parseInt(digits[9])) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
    d = 11 - (sum % 11);
    if (d >= 10) d = 0;
    return d === parseInt(digits[10]);
  }
  if (digits.length === 14) {
    if (/^(\d)\1{13}$/.test(digits)) return false;
    let size = 12; let sum = 0; let pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += parseInt(digits[size - i]) * pos--;
      if (pos < 2) pos = 9;
    }
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits[12])) return false;
    size = 13; sum = 0; pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += parseInt(digits[size - i]) * pos--;
      if (pos < 2) pos = 9;
    }
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    return result === parseInt(digits[13]);
  }
  return false;
}

// ─── MFA Step Component ────────────────────────────────────────────────────

interface MfaStepProps {
  challengeId: string;
  deviceFingerprint: string;
  loginValue: string;
  onSuccess: (data: ApiLoginSuccess) => void;
  onBack: () => void;
}

interface ApiLoginSuccess {
  accessToken: string;
  refreshToken: string;
  sessionId: string;
  expiresIn: number;
  tokenType: string;
  riskLevel: string;
}

function MfaStep({ challengeId, deviceFingerprint, loginValue, onSuccess, onBack }: MfaStepProps) {
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const otp = otpDigits.join('');

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    setError(null);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newDigits = pasted.split('').concat(Array(6).fill('')).slice(0, 6);
      setOtpDigits(newDigits);
      const lastFilled = Math.min(pasted.length, 5);
      inputRefs.current[lastFilled]?.focus();
      e.preventDefault();
    }
  };

  const handleSubmitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 6) {
      setError('Digite os 6 dígitos do código OTP');
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const mfaPayload = {
        challengeId,
        otpCode: otp,
        deviceFingerprint,
      };

      const data = await validateMfa(mfaPayload);
      onSuccess(data);
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      const { details } = getErrorDetails(err);
      setError(errorMsg);
      setErrorDetails(details ?? []);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* MFA Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <i className="ri-shield-keyhole-line text-3xl text-orange-600"></i>
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Verificação de Segurança</h2>
        <p className="text-gray-500 text-sm leading-relaxed">
          Um código de verificação foi enviado para o seu e-mail ou celular cadastrado.
          <br />
          Digite os 6 dígitos para continuar.
        </p>
      </div>

      {/* Login info badge */}
      <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
        <i className="ri-user-line text-orange-500"></i>
        <span className="text-sm text-orange-800 font-medium">{loginValue}</span>
        <button
          type="button"
          onClick={onBack}
          className="ml-auto text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-edit-line"></i>
          Alterar
        </button>
      </div>

      <form onSubmit={handleSubmitMfa} className="space-y-6">
        {/* OTP Input */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-4 text-center">
            Código OTP (6 dígitos)
          </label>
          <div className="flex gap-3 justify-center" onPaste={handlePaste}>
            {otpDigits.map((digit, index) => (
              <input
                key={index}
                ref={el => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleDigitChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all ${
                  error
                    ? 'border-red-300 bg-red-50 text-red-700'
                    : digit
                    ? 'border-orange-400 bg-orange-50 text-orange-700'
                    : 'border-gray-200 bg-gray-50 text-gray-800'
                }`}
                autoFocus={index === 0}
              />
            ))}
          </div>
          {error && (
            <div className="mt-3 flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0 text-red-500"></i>
              <div className="flex-1">
                <p className="text-sm text-red-600 font-medium">{error}</p>
                {errorDetails.length > 0 && (
                  <ul className="mt-1.5 space-y-1">
                    {errorDetails.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-red-500">
                        <i className="ri-close-circle-line mt-0.5 flex-shrink-0 text-red-400"></i>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Submit MFA */}
        <button
          type="submit"
          disabled={isLoading || otp.length < 6}
          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 px-6 rounded-xl font-semibold text-base hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
        >
          {isLoading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-xl"></i>
              Verificando...
            </>
          ) : (
            <>
              <i className="ri-shield-check-line text-xl"></i>
              Verificar e Entrar
            </>
          )}
        </button>

        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 px-6 rounded-xl font-semibold text-sm hover:border-gray-300 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Voltar ao Login
        </button>
      </form>

      {/* Help text */}
      <div className="text-center">
        <p className="text-xs text-gray-400">
          Não recebeu o código?{' '}
          <span className="text-orange-500 font-medium">
            Verifique sua caixa de spam ou aguarde alguns instantes.
          </span>
        </p>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────

type LoginStep = 'credentials' | 'mfa';

export default function PremiadoLogin() {
  const [showPassword, setShowPassword] = useState(false);
  const [showTermos, setShowTermos] = useState(false);
  const [showPrivacidade, setShowPrivacidade] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [tipoDoc, setTipoDoc] = useState<'cpf' | 'cnpj'>('cpf');
  const [loginStep, setLoginStep] = useState<LoginStep>('credentials');
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiErrorDetails, setApiErrorDetails] = useState<string[]>([]);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const deviceFingerprintRef = useRef<string>('');

  const [formData, setFormData] = useState({
    login: '',
    password: '',
    rememberMe: false,
  });

  const [errors, setErrors] = useState<{ login?: string; password?: string }>({});

  const handleTipoDocChange = (tipo: 'cpf' | 'cnpj') => {
    setTipoDoc(tipo);
    setFormData(prev => ({ ...prev, login: '' }));
    setErrors(prev => ({ ...prev, login: undefined }));
    setApiErrorDetails([]);
  };

  const validateForm = () => {
    const newErrors: { login?: string; password?: string } = {};
    if (!formData.login.trim()) {
      newErrors.login = 'Campo obrigatório';
    } else {
      const digits = formData.login.replace(/\D/g, '');
      const expectedLen = tipoDoc === 'cpf' ? 11 : 14;
      if (digits.length !== expectedLen) {
        newErrors.login = tipoDoc === 'cpf' ? 'CPF incompleto (11 dígitos)' : 'CNPJ incompleto (14 dígitos)';
      } else if (!validateCpfCnpj(formData.login)) {
        newErrors.login = tipoDoc === 'cpf' ? 'CPF inválido' : 'CNPJ inválido';
      }
    }
    if (!formData.password) {
      newErrors.password = 'Campo obrigatório';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Senha deve ter no mínimo 6 caracteres';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    let newValue: string | boolean;
    if (name === 'login') {
      newValue = formatDoc(value, tipoDoc);
    } else if (type === 'checkbox') {
      newValue = checked;
    } else {
      newValue = value;
    }
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    setApiError(null);
    setApiErrorDetails([]);
  };

  const handleMfaSuccess = (data: {
    accessToken: string;
    refreshToken: string;
    sessionId: string;
    expiresIn: number;
    tokenType: string;
    riskLevel: string;
  }) => {
    login({
      userId: formData.login.replace(/\D/g, ''),
      nome: formData.login,
      email: formData.login,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      sessionId: data.sessionId,
      expiresIn: data.expiresIn,
      tokenType: data.tokenType,
      riskLevel: data.riskLevel,
      deviceFingerprint: deviceFingerprintRef.current,
    });
    setShowSuccessAnimation(true);
  };

  const handleAnimationComplete = () => {
    const from = (location.state as { from?: string })?.from || '/premiado/home';
    navigate(from, { replace: true });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setApiError(null);
    setApiErrorDetails([]);

    try {
      const loginPayload = {
        login: formData.login.replace(/\D/g, ''),
        password: formData.password,
        deviceFingerprint: generateDeviceFingerprint(),
      };

      deviceFingerprintRef.current = loginPayload.deviceFingerprint;

      const data = await loginService(loginPayload);

      login({
        userId: formData.login.replace(/\D/g, ''),
        nome: formData.login,
        email: formData.login,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        sessionId: data.sessionId,
        expiresIn: data.expiresIn,
        tokenType: data.tokenType,
        riskLevel: data.riskLevel,
        deviceFingerprint: loginPayload.deviceFingerprint,
      });
      setShowSuccessAnimation(true);
      return;
    } catch (err) {
      if (isApiException(err) && err.status === 401 && err.error === 'MFA Required') {
        const challengeId = err.details?.find((d) => d.startsWith('challengeId='))?.split('=')[1] ?? null;
        if (challengeId) {
          setMfaChallengeId(challengeId);
          setLoginStep('mfa');
          setIsLoading(false);
          return;
        }
      }

      const errorMsg = getErrorMessage(err);
      const { details } = getErrorDetails(err);
      setApiError(errorMsg);
      setApiErrorDetails(details ?? []);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 p-16 flex-col justify-between relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full -ml-48 -mb-48"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-12">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-lg">
                <i className="ri-trophy-line text-3xl text-white"></i>
              </div>
              <div>
                <span className="text-2xl font-bold text-white block">Área do Premiado</span>
                <span className="text-orange-100 text-sm">Sua jornada de conquistas</span>
              </div>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
              Transforme seu<br />
              desempenho em<br />
              <span className="text-orange-200">recompensas</span>
            </h1>
            <p className="text-orange-50 text-lg leading-relaxed max-w-md">
              Acesse sua conta e acompanhe suas conquistas, pontos, distintivos e troque por prêmios exclusivos.
            </p>
          </div>

          <div className="relative z-10 grid grid-cols-1 gap-4">
            {[
              { icon: 'ri-gift-line', title: 'Recompensas Exclusivas', desc: 'Troque seus pontos por prêmios incríveis e experiências únicas' },
              { icon: 'ri-line-chart-line', title: 'Acompanhe seu Progresso', desc: 'Veja sua evolução, rankings e performance em tempo real' },
              { icon: 'ri-medal-line', title: 'Distintivos e Conquistas', desc: 'Desbloqueie badges especiais e mostre suas conquistas' },
            ].map(item => (
              <div key={item.title} className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all duration-300 group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">{item.title}</h3>
                    <p className="text-orange-100 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side */}
        <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                <i className="ri-trophy-line text-3xl text-white"></i>
              </div>
              <div>
                <span className="text-2xl font-bold text-gray-900 block">Área do Premiado</span>
                <span className="text-gray-500 text-sm">Sua jornada de conquistas</span>
              </div>
            </div>

            {/* Card */}
            <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100">

              {/* ── STEP: MFA ────────────────────────────────────── */}
              {loginStep === 'mfa' && mfaChallengeId ? (
                <MfaStep
                  challengeId={mfaChallengeId}
                  deviceFingerprint={deviceFingerprintRef.current}
                  loginValue={formData.login}
                  onSuccess={handleMfaSuccess}
                  onBack={() => {
                    setLoginStep('credentials');
                    setMfaChallengeId(null);
                    setApiError(null);
                    setApiErrorDetails([]);
                  }}
                />
              ) : (
                /* ── STEP: CREDENTIALS ──────────────────────────── */
                <>
                  <div className="mb-10">
                    <div className="flex justify-center mb-6">
                      <img
                        src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/6356cf72c36f3ef424056fffdf6d6f31.png"
                        alt="GetPoints Logo"
                        className="h-12 w-auto"
                      />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-2 text-center">
                      Bem-vindo de volta!
                    </h2>
                    <p className="text-gray-600 text-base text-center">Use seu CPF ou CNPJ e senha para acessar</p>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-6">
                    {/* Seletor CPF / CNPJ */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tipo de documento
                      </label>
                      <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
                        {(['cpf', 'cnpj'] as const).map(tipo => (
                          <button
                            key={tipo}
                            type="button"
                            onClick={() => handleTipoDocChange(tipo)}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                              tipoDoc === tipo
                                ? 'bg-white text-orange-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            <i className={tipo === 'cpf' ? 'ri-user-line text-base' : 'ri-building-line text-base'}></i>
                            {tipo.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* CPF/CNPJ */}
                    <div>
                      <label htmlFor="login" className="block text-sm font-semibold text-gray-700 mb-2">
                        {tipoDoc === 'cpf' ? 'CPF' : 'CNPJ'}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className="ri-id-card-line text-lg text-gray-400"></i>
                        </div>
                        <input
                          id="login"
                          name="login"
                          type="text"
                          inputMode="numeric"
                          value={formData.login}
                          onChange={handleChange}
                          className={`block w-full pl-12 pr-4 py-3.5 border ${errors.login ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-base tracking-wide`}
                          placeholder={tipoDoc === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'}
                          maxLength={tipoDoc === 'cpf' ? 14 : 18}
                        />
                      </div>
                      {errors.login && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>
                          {errors.login}
                        </p>
                      )}
                    </div>

                    {/* Password */}
                    <div>
                      <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                        Senha
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className="ri-lock-line text-lg text-gray-400"></i>
                        </div>
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={handleChange}
                          className={`block w-full pl-12 pr-12 py-3.5 border ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-base`}
                          placeholder="Digite sua senha"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center hover:bg-gray-100 rounded-r-xl transition-colors cursor-pointer"
                        >
                          <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg text-gray-400 hover:text-gray-600`}></i>
                        </button>
                      </div>
                      {errors.password && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>
                          {errors.password}
                        </p>
                      )}
                    </div>

                    {/* API Error */}
                    {apiError && (
                      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                        <i className="ri-error-warning-fill text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                        <div className="flex-1">
                          <p className="text-sm text-red-700 leading-relaxed font-medium">{apiError}</p>
                          {apiErrorDetails.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {apiErrorDetails.map((detail, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-red-600">
                                  <i className="ri-close-circle-line mt-0.5 flex-shrink-0 text-red-400"></i>
                                  <span>{detail}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Remember + Forgot */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2">
                      <label className="flex items-center cursor-pointer group">
                        <input
                          type="checkbox"
                          name="rememberMe"
                          checked={formData.rememberMe}
                          onChange={handleChange}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                        />
                        <span className="ml-2 text-sm text-gray-700 group-hover:text-gray-900 transition-colors">Lembrar-me</span>
                      </label>
                      <a
                        href="/premiado/recuperar-senha"
                        className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors whitespace-nowrap"
                      >
                        Esqueci minha senha
                      </a>
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 px-6 rounded-xl font-semibold text-base hover:from-orange-600 hover:to-orange-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      {isLoading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-xl"></i>
                          Entrando...
                        </>
                      ) : (
                        <>
                          Entrar na Conta
                          <i className="ri-arrow-right-line text-xl"></i>
                        </>
                      )}
                    </button>

                    {/* Divider */}
                    <div className="relative py-2">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-white text-gray-500">Não tem uma conta?</span>
                      </div>
                    </div>

                    {/* Create Account */}
                    <button
                      type="button"
                      onClick={() => navigate('/premiado/cadastro')}
                      className="w-full bg-white border-2 border-gray-200 text-gray-700 py-4 px-6 rounded-xl font-semibold text-base hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-user-add-line text-xl"></i>
                      Criar Nova Conta
                    </button>
                  </form>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex items-center justify-center gap-3 mb-4">
                <img
                  src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/6356cf72c36f3ef424056fffdf6d6f31.png"
                  alt="GetPoints Logo"
                  className="h-10 w-auto object-contain"
                />
              </div>
              <p className="text-center text-sm text-slate-600 mb-3">
                © 2025 GetPoints Tecnologia, C.R.I.E. Ltda. Todos os direitos reservados.
              </p>
              <div className="flex items-center justify-center gap-4 text-sm">
                <button onClick={() => setShowTermos(true)} className="text-slate-600 hover:text-orange-600 transition-colors cursor-pointer">
                  Termos de Uso
                </button>
                <span className="text-slate-300">•</span>
                <button onClick={() => setShowPrivacidade(true)} className="text-slate-600 hover:text-orange-600 transition-colors cursor-pointer">
                  Política de Privacidade
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Support Widget */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => {
            const modal = document.getElementById('chat-modal-login');
            if (modal) modal.classList.remove('hidden');
          }}
          className="w-14 h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group cursor-pointer"
        >
          <i className="ri-message-3-line text-2xl group-hover:scale-110 transition-transform"></i>
        </button>
      </div>

      {/* Chat Modal */}
      <div id="chat-modal-login" className="hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <i className="ri-customer-service-2-line text-xl text-white"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Chat Online</h3>
                <p className="text-sm text-gray-500">Estamos aqui para ajudar</p>
              </div>
            </div>
            <button
              onClick={() => {
                const modal = document.getElementById('chat-modal-login');
                if (modal) modal.classList.add('hidden');
              }}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-500"></i>
            </button>
          </div>
          <div className="p-6 h-96 overflow-y-auto bg-gray-50">
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-customer-service-2-line text-white text-sm"></i>
              </div>
              <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-sm max-w-[80%]">
                <p className="text-sm text-gray-700">Olá! Como posso ajudá-lo hoje?</p>
                <span className="text-xs text-gray-400 mt-1 block">Agora</span>
              </div>
            </div>
          </div>
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Digite sua mensagem..."
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
              />
              <button className="w-10 h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-xl flex items-center justify-center transition-colors cursor-pointer">
                <i className="ri-send-plane-fill text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      {showTermos && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '85vh' }}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <i className="ri-file-text-line text-xl text-white"></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Termos de Uso</h2>
                  <p className="text-sm text-gray-500">GetPoints - Plataforma de Incentivos</p>
                </div>
              </div>
              <button onClick={() => setShowTermos(false)} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-2xl text-gray-500"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-orange-800"><strong>Última atualização:</strong> Janeiro de 2026</p>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Ao acessar e utilizar a plataforma GetPoints, você concorda em cumprir e estar vinculado aos termos e condições de uso. A plataforma é fornecida para gestão de programas de incentivo, distribuição de pontos e gerenciamento de recompensas. Para dúvidas, entre em contato: contato@getpoints.com.br
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button onClick={() => setShowTermos(false)} className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-check-line text-lg"></i>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal */}
      {showPrivacidade && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col" style={{ height: '85vh' }}>
            <div className="flex-shrink-0 p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                    <i className="ri-shield-check-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">Política de Privacidade</h2>
                    <p className="text-sm text-slate-500">Última atualização: Janeiro de 2025</p>
                  </div>
                </div>
                <button onClick={() => setShowPrivacidade(false)} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-2xl text-slate-600"></i>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-slate-600 leading-relaxed">
                A <strong className="text-orange-600">Área do Premiado</strong> está comprometida em proteger a privacidade e os dados pessoais de seus usuários, em conformidade com a LGPD (Lei nº 13.709/2018). Coletamos dados necessários para fornecer os serviços da plataforma, incluindo nome, CPF/CNPJ, e-mail e informações de desempenho. Para dúvidas ou solicitações de dados: dpo@getpoints.com.br
              </p>
            </div>
            <div className="flex-shrink-0 p-6 border-t border-slate-200 bg-slate-50">
              <button onClick={() => setShowPrivacidade(false)} className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all cursor-pointer">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Login Success Animation */}
      {showSuccessAnimation && (
        <LoginSuccessAnimation
          userName={formData.login.replace(/\D/g, '')}
          onComplete={handleAnimationComplete}
        />
      )}
    </>
  );
}